import { SECOND, useNow, useTimer } from '@speedlo/hooks'
import { differenceInMinutes } from 'date-fns'

import { FCompanyBranchStatusFragment } from '../graph/generated'

export type TStatus = FCompanyBranchStatusFragment

export type TStatusDecision = 'Open' | 'Closed' | 'ClosingSoon' | 'OpeningSoon'

interface IProps {
  status: Maybe<TStatus>
  updateInterval?: number
  openSoonThresholdInMinutes?: number
  closeSoonThresholdInMinutes?: number
  onRefresh?(): void
}

export interface IShopStatus {
  decision: TStatusDecision
  openingIn: number
  closingIn: number
  closedNote: Maybe<string>
}

export function useShopStatus({
  status,
  updateInterval = 30 * SECOND,
  openSoonThresholdInMinutes = 15,
  closeSoonThresholdInMinutes = 30,
}: IProps) {
  const getNow = useNow()

  useTimer({
    interval: updateInterval,
    enabled: updateInterval > 0,
  })

  const now = getNow()

  if (status === null) {
    return {
      decision: 'Closed' as TStatusDecision,
      openingIn: -1,
      closingIn: -1,
      closedNote: null,
    }
  }

  const { isOpen, willCloseAt, willOpenAt } = status

  let decision: TStatusDecision

  const openingIn = willOpenAt ? differenceInMinutes(willOpenAt, now) : 0
  const closingIn = willCloseAt ? differenceInMinutes(willCloseAt, now) : 0

  if (isOpen) {
    if (willCloseAt && closingIn <= 0) {
      decision = 'Closed'
    } else {
      decision = isSoon(willCloseAt, closeSoonThresholdInMinutes, now)
        ? 'ClosingSoon'
        : 'Open'
    }
  } else {
    if (willOpenAt && openingIn <= 0) {
      decision = 'Open'
    } else {
      decision = isSoon(willOpenAt, openSoonThresholdInMinutes, now)
        ? 'OpeningSoon'
        : 'Closed'
    }
  }

  const closedNote = status.currentInterval
    ? status.currentInterval.closedNote
    : null

  const shopStatus: IShopStatus = {
    decision,
    openingIn,
    closingIn,
    closedNote,
  }
  return shopStatus
}

function isSoon(dateTime: Maybe<Date>, threshold: number, now: Date) {
  return dateTime
    ? Math.abs(differenceInMinutes(dateTime, now)) <= threshold
    : false
}
